<div class="call-setup-container">
  <!-- Step Progress -->
  <div class="step-progress-container">
    <app-step-progress
      [steps]="stepLabels"
      [currentStep]="displayedCurrentStep"
      [completedSteps]="completedSteps"
      [disabled]="false"
      (stepClick)="onStepClick($event)"
    ></app-step-progress>
    @if (isUploadedCall && hasRecordingUploaded && call.templateApprovedByUser && !call.isPrevious) {
      @if (completeSetupMutation.result$ | async; as result) {
        <button mat-flat-button class="save-continue-button" (click)="completeSetup()" [disabled]="result.isPending">
          @if (result.isPending) {
            <mat-spinner diameter="18"></mat-spinner>
          } @else {
            {{ 'MEETING_SETUP.COMPLETE_SETUP.SAVE_CONTINUE' | translate }}
          }
        </button>
      }
    }
    @if (!call.isUpcoming && !call.isOngoing) {
      <button mat-flat-button class="view-meeting-button" [routerLink]="['/app', 'meetings', '' + call.id]">
        {{ 'MEETING_SETUP.COMPLETE_SETUP.VIEW_MEETING' | translate }}
      </button>
    }
  </div>

  <div class="setup-content">
    <div class="main-content">
      <!-- Step 1: Meeting Details -->
      @if (call !== null && metadata !== null) {
        <app-meeting-details-card
          id="step-0"
          [call]="call"
          [metadata]="metadata"
          [canEdit]="canEdit"
          [stepNumber]="meetingDetailsStepNumber"
          [title]="'MEETING_SETUP.MEETING_DETAILS.STEP_TITLE' | translate"
          [description]="call.agendaExtractionProgress !== 100 ? ('MEETING_SETUP.MEETING_DETAILS.SUBTITLE' | translate) : null"
          (detailsSaved)="onDetailsSaved($event)"
        ></app-meeting-details-card>
      }

      <!-- Step 2: Agenda Type -->
      @if (call !== null && !isUploadedCall) {
        <div class="step-card" id="step-1">
          <div class="step-header">
            <div class="step-header-content">
              <h3>
                {{ 'MEETING_SETUP.STEP_LABEL' | translate: { number: agendaTypeStepNumber } }} {{ 'MEETING_SETUP.AGENDA_TYPE.STEP_TITLE' | translate }}
                <mat-icon class="info-icon" [matTooltip]="'MEETING_SETUP.AGENDA_TYPE.TOOLTIP' | translate">info_outline</mat-icon>
              </h3>
              <p>{{ 'MEETING_SETUP.AGENDA_TYPE.DESCRIPTION' | translate }}</p>
            </div>
          </div>

          <div class="options-container">
            <mat-radio-group
              [ngModel]="call.agendaType"
              (ngModelChange)="updateAgendaType($event)"
              [disabled]="!canEdit || isAgendaTypeLocked"
              class="agenda-type-options"
            >
              <label class="option-card" [class.selected]="call.agendaType === 'SINGLE'" [class.disabled]="!canEdit || isAgendaTypeLocked">
                <mat-radio-button value="SINGLE" [disabled]="!canEdit || isAgendaTypeLocked"></mat-radio-button>
                <div class="option-content">
                  <span class="option-title">{{ 'MEETING_SETUP.AGENDA_TYPE.SINGLE' | translate }}</span>
                  <span class="option-description">{{ 'MEETING_SETUP.AGENDA_TYPE.SINGLE_DESCRIPTION' | translate }}</span>
                </div>
              </label>

              <label
                class="option-card"
                [class.selected]="call.agendaType === 'MULTIPLE'"
                [class.disabled]="!canEdit || isAgendaTypeLocked || isExtractingAgenda"
              >
                <mat-radio-button value="MULTIPLE" [disabled]="!canEdit || isAgendaTypeLocked || isExtractingAgenda"></mat-radio-button>
                <div class="option-content">
                  <span class="option-title">{{ 'MEETING_SETUP.AGENDA_TYPE.MULTIPLE' | translate }}</span>
                  <span class="option-description">{{ 'MEETING_SETUP.AGENDA_TYPE.MULTIPLE_DESCRIPTION' | translate }}</span>
                </div>
              </label>
            </mat-radio-group>

            @if (isMultipleAgenda) {
              <a class="multi-agenda-help-link" href="https://help.wiseworks.ai/en/articles/470601-multi-agenda-meetings" target="_blank" rel="noopener">
                {{ 'MEETING_SETUP.AGENDA_TYPE.LEARN_MORE' | translate }}
              </a>
            }
          </div>
        </div>
      }

      <!-- Step 3: Template Source (Step 2 for uploaded calls) -->
      @if (call !== null && currentStep >= (isUploadedCall ? 2 : 3)) {
        <div class="step-card" id="step-2">
          <div class="step-header">
            <div class="step-header-content">
              <h3>
                {{ 'MEETING_SETUP.STEP_LABEL' | translate: { number: templateSourceStepNumber } }} {{ 'MEETING_SETUP.TEMPLATE_SOURCE.STEP_TITLE' | translate }}
                <mat-icon class="info-icon" [matTooltip]="'MEETING_SETUP.TEMPLATE_SOURCE.TOOLTIP' | translate">info_outline</mat-icon>
              </h3>
              <p>{{ 'MEETING_SETUP.TEMPLATE_SOURCE.DESCRIPTION' | translate }}</p>
            </div>
          </div>

          <div class="options-container">
            @if (isMultipleAgenda) {
              <div class="multiple-agenda-info">
                <mat-icon>info_outline</mat-icon>
                <p>{{ 'MEETING_SETUP.TEMPLATE_SOURCE.MULTIPLE_AGENDA_INFO' | translate }}</p>
              </div>
            } @else {
              <app-template-source-picker
                #templateSourcePicker
                [canEdit]="canEdit && !isExtractingAgenda"
                [isImporting]="isImportingAgenda"
                [maxAgendaSize]="maxAgendaSize"
                [hasAgendaUploaded]="hasAgendaUploaded"
                [uploadedFileName]="uploadedAgendaFileName"
                [templateSourceType]="call.templateSourceType"
                [requiresConfirmation]="shouldConfirmTemplateReplace"
                (agendaFileChange)="onAgendaFileChange($event)"
                (confirmationRequested)="onUploadConfirmationRequested()"
                (createNewClicked)="createBlankTemplate()"
                (loadSavedClicked)="openTemplateSelectionDialog()"
              />
            }
          </div>
        </div>
      }

      <!-- Step 4: Template Review (visible after step 3 or when processing, only for single agenda) -->
      @if (!isMultipleAgenda && (currentStep >= (isUploadedCall ? 3 : 4) || isExtractingAgenda) && call !== null && metadata !== null) {
        <div class="step-card" id="step-3">
          <div class="step-header">
            <div class="step-header-content">
              <h3>
                {{ 'MEETING_SETUP.STEP_LABEL' | translate: { number: reviewStepNumber } }} {{ 'MEETING_SETUP.TEMPLATE_REVIEW.STEP_TITLE' | translate }}
                <mat-icon class="info-icon" [matTooltip]="'MEETING_SETUP.TEMPLATE_REVIEW.TOOLTIP' | translate">info_outline</mat-icon>
              </h3>
              <p>{{ 'MEETING_SETUP.TEMPLATE_REVIEW.DESCRIPTION' | translate }}</p>
            </div>
            @if (!isExtractingAgenda && !isAgendaExtractionFailed && canEdit && (!call.templateApprovedByUser || templateEditor?.hasUnsavedChanges)) {
              <span
                class="confirm-button-wrapper"
                [matTooltip]="hasTemplateItems ? null : ('MEETING_SETUP.TEMPLATE_REVIEW.CONFIRM_DISABLED_NO_ITEMS' | translate)"
              >
                <button mat-flat-button class="confirm-button" (click)="confirmTemplate()" [disabled]="!hasTemplateItems || isParticipantExtractionInProgress">
                  {{ (isUploadedCall ? 'MEETING_SETUP.TEMPLATE_REVIEW.CONFIRM_AND_CONTINUE' : 'MEETING_SETUP.TEMPLATE_REVIEW.CONFIRM') | translate }}
                </button>
              </span>
            }
          </div>

          @if (isAgendaExtractionFailed) {
            <div class="extraction-failed-container">
              <mat-icon class="error-icon">error_outline</mat-icon>
              <span class="error-text">{{ 'MEETING_SETUP.TEMPLATE_REVIEW.EXTRACTION_FAILED' | translate }}</span>
              <p class="error-description">{{ 'MEETING_SETUP.TEMPLATE_REVIEW.EXTRACTION_FAILED_DESCRIPTION' | translate }}</p>
            </div>
          } @else if (isExtractingAgenda) {
            <div class="processing-container">
              <div class="processing-header">
                <span class="processing-text">{{ 'MEETING_SETUP.TEMPLATE_REVIEW.EXTRACTING' | translate }}</span>
                @if (agendaExtractionProgress !== null && agendaExtractionProgress > 0) {
                  <span class="processing-percentage">{{ agendaExtractionProgress }}%</span>
                }
              </div>
              @if (agendaExtractionProgress !== null && agendaExtractionProgress > 0) {
                <mat-progress-bar mode="determinate" [value]="agendaExtractionProgress" class="processing-bar"></mat-progress-bar>
              } @else {
                <mat-progress-bar mode="indeterminate" class="processing-bar"></mat-progress-bar>
              }

              <!-- Skeleton tabs -->
              <div class="skeleton-tabs">
                <div class="skeleton-tab"></div>
                <div class="skeleton-tab"></div>
                <div class="skeleton-tab"></div>
              </div>

              <!-- Skeleton items -->
              <div class="skeleton-content">
                @for (item of [1, 2, 3, 4]; track item) {
                  <div class="skeleton-item">
                    <div class="skeleton-checkbox"></div>
                    <div class="skeleton-number"></div>
                    <div class="skeleton-text" [style.width.%]="40 + item * 10"></div>
                  </div>
                }
              </div>
            </div>
          } @else {
            <div class="tabs-container">
              <mat-tab-group [(selectedIndex)]="selectedTabIndex" animationDuration="200ms">
                <!-- Template Items Tab -->
                @if (templateItems$ | async; as templateItems) {
                  <mat-tab>
                    <ng-template mat-tab-label>
                      <mat-icon>list_alt</mat-icon>
                      <span>{{ 'MEETING_SETUP.TEMPLATE_REVIEW.TABS.TEMPLATE' | translate }}</span>
                      @if (templateItems.length > 0) {
                        <span class="tab-badge">{{ templateItems.length }}</span>
                      }
                    </ng-template>
                    <div class="tab-content template-tab">
                      <app-template-editor
                        #templateEditor
                        [templateItems]="templateItems"
                        [availableResources]="call.userUploadedResources"
                        [allowFileUpload]="true"
                        [attendeeNames]="attendeeNames"
                        [defaultLevelOfDetail]="defaultLevelOfDetail"
                        [defaultAnonymization]="defaultAnonymization"
                        [readonly]="!canEdit"
                        [showProcessingStatus]="call.status === 'COMPLETED' && !call.isPostProcessingComplete"
                        (templateSaved)="onTemplateSaved($event)"
                        (saveAsRequested)="onSaveAsTemplate($event)"
                        (fileUploadRequested)="handleFileUpload($event)"
                      ></app-template-editor>
                    </div>
                  </mat-tab>
                }

                <!-- Attendees Tab -->
                <mat-tab>
                  <ng-template mat-tab-label>
                    <mat-icon>people</mat-icon>
                    <span>{{ 'MEETING_SETUP.TEMPLATE_REVIEW.TABS.ATTENDEES' | translate }}</span>
                    @if (metadata.attendees.length > 0) {
                      <span class="tab-badge">{{ metadata.attendees.length }}</span>
                    } @else if (call.agendaExtractionProgress === 100) {
                      <mat-icon class="tab-warning-icon" [matTooltip]="'MEETING_SETUP.TEMPLATE_REVIEW.NO_ATTENDEES_WARNING' | translate">error</mat-icon>
                    }
                  </ng-template>
                  <div class="tab-content attendees-tab">
                    <div class="attendees-header">
                      <p class="attendees-description">
                        {{ 'MEETING_SETUP.TEMPLATE_REVIEW.ATTENDEES_DESCRIPTION' | translate }}
                      </p>
                      <div class="attendees-actions">
                        @if (canEdit) {
                          <app-file-upload
                            #participantExtractionFileUpload
                            class="hidden-file-upload"
                            [acceptedFileTypes]="['pdf']"
                            [maxSize]="maxAgendaSize"
                            type="agenda"
                            (progressChange)="onParticipantExtractionUploadStarted()"
                            (fileChange)="onParticipantExtractionFileChange($event)"
                          ></app-file-upload>
                          <button mat-stroked-button (click)="participantExtractionFileUpload.openFileDialog()" [disabled]="isParticipantExtractionInProgress">
                            <mat-icon>auto_fix_high</mat-icon>
                            {{ 'MEETING_SETUP.EXTRACT_PARTICIPANTS.BUTTON' | translate }}
                          </button>
                          <button mat-stroked-button (click)="showAddAttendeeForm = !showAddAttendeeForm" [disabled]="isParticipantExtractionInProgress">
                            <mat-icon>person_add</mat-icon>
                            {{ 'MEETING_SETUP.TEMPLATE_REVIEW.ADD_ATTENDEE' | translate }}
                          </button>
                        }
                      </div>
                    </div>

                    <!-- Participant Extraction In Progress -->
                    @if (isParticipantExtractionInProgress) {
                      <div class="extraction-in-progress-banner">
                        <mat-spinner diameter="20"></mat-spinner>
                        <span>{{ 'MEETING_SETUP.EXTRACT_PARTICIPANTS.IN_PROGRESS' | translate }}</span>
                      </div>
                    }

                    <!-- Add Attendee Form -->
                    @if (showAddAttendeeForm && canEdit && !isParticipantExtractionInProgress) {
                      <div class="add-attendee-form">
                        <div class="form-row" [class.with-placeholder]="isGoverningBodyBoard">
                          <mat-form-field appearance="outline">
                            <mat-label>{{ 'MEETING_SETUP.TEMPLATE_REVIEW.HONORIFIC' | translate }}</mat-label>
                            <input matInput [(ngModel)]="newAttendee.honorifics" />
                          </mat-form-field>
                          <mat-form-field appearance="outline">
                            <mat-label>{{ 'MEETING_SETUP.TEMPLATE_REVIEW.FIRST_NAME' | translate }}</mat-label>
                            <input matInput [(ngModel)]="newAttendee.firstName" />
                          </mat-form-field>
                          <mat-form-field appearance="outline">
                            <mat-label>{{ 'MEETING_SETUP.TEMPLATE_REVIEW.LAST_NAME' | translate }}</mat-label>
                            <input matInput [(ngModel)]="newAttendee.lastName" />
                          </mat-form-field>
                          <mat-form-field appearance="outline">
                            <mat-label>{{ 'MEETING_SETUP.TEMPLATE_REVIEW.TYPE' | translate }}</mat-label>
                            <mat-select [(ngModel)]="newAttendee.type">
                              @for (type of attendeeTypes; track type) {
                                <mat-option [value]="type">
                                  {{ 'MEETING_SETUP.TEMPLATE_REVIEW.ATTENDEE_TYPES.' + type | translate }}
                                </mat-option>
                              }
                            </mat-select>
                          </mat-form-field>
                          <mat-form-field appearance="outline">
                            <mat-label>{{ 'MEETING_SETUP.TEMPLATE_REVIEW.ROLE' | translate }}</mat-label>
                            <input matInput [(ngModel)]="newAttendee.role" />
                          </mat-form-field>
                          @if (isGoverningBodyBoard) {
                            <mat-form-field appearance="outline">
                              <mat-label>{{ 'MEETING_SETUP.TEMPLATE_REVIEW.PLACEHOLDER_NAME' | translate }}</mat-label>
                              <input matInput [(ngModel)]="newAttendee.placeholderName" />
                            </mat-form-field>
                          }
                        </div>
                        <div class="form-actions">
                          <button mat-button (click)="cancelAddAttendee()">
                            {{ 'COMMON.CANCEL' | translate }}
                          </button>
                          <button mat-flat-button color="primary" [disabled]="!newAttendee.firstName || !newAttendee.lastName" (click)="addNewAttendee()">
                            {{ 'MEETING_SETUP.TEMPLATE_REVIEW.ADD_ATTENDEE' | translate }}
                          </button>
                        </div>
                      </div>
                    }

                    <!-- Attendee List -->
                    @if (metadata.attendees.length === 0 && !isParticipantExtractionInProgress) {
                      <div class="empty-state">
                        <mat-icon>person_add</mat-icon>
                        <p>{{ 'MEETING_SETUP.TEMPLATE_REVIEW.NO_ATTENDEES' | translate }}</p>
                      </div>
                    } @else if (metadata.attendees.length > 0) {
                      <div class="attendees-list">
                        @for (group of groupedAttendees; track group.type) {
                          <div class="attendee-group">
                            <h4 class="group-title">
                              {{ 'MEETING_SETUP.TEMPLATE_REVIEW.ATTENDEE_TYPES.' + group.type | translate | uppercase }}
                            </h4>
                            <div class="group-attendees">
                              @for (attendee of group.attendees; track attendee.id) {
                                <div class="attendee-row" [class.editing]="editedAttendeeId === attendee.id">
                                  @if (editedAttendeeId === attendee.id) {
                                    <!-- Editing mode -->
                                    <div class="attendee-edit-form">
                                      <div class="form-row" [class.with-placeholder]="isGoverningBodyBoard">
                                        <mat-form-field appearance="outline">
                                          <mat-label>{{ 'MEETING_SETUP.TEMPLATE_REVIEW.HONORIFIC' | translate }}</mat-label>
                                          <input matInput [(ngModel)]="editedAttendeeFormData.honorifics" />
                                        </mat-form-field>
                                        <mat-form-field appearance="outline">
                                          <mat-label>{{ 'MEETING_SETUP.TEMPLATE_REVIEW.FIRST_NAME' | translate }}</mat-label>
                                          <input matInput [(ngModel)]="editedAttendeeFormData.firstName" />
                                        </mat-form-field>
                                        <mat-form-field appearance="outline">
                                          <mat-label>{{ 'MEETING_SETUP.TEMPLATE_REVIEW.LAST_NAME' | translate }}</mat-label>
                                          <input matInput [(ngModel)]="editedAttendeeFormData.lastName" />
                                        </mat-form-field>
                                        <mat-form-field appearance="outline">
                                          <mat-label>{{ 'MEETING_SETUP.TEMPLATE_REVIEW.TYPE' | translate }}</mat-label>
                                          <mat-select [(ngModel)]="editedAttendeeFormData.type">
                                            @for (type of attendeeTypes; track type) {
                                              <mat-option [value]="type">
                                                {{ 'MEETING_SETUP.TEMPLATE_REVIEW.ATTENDEE_TYPES.' + type | translate }}
                                              </mat-option>
                                            }
                                          </mat-select>
                                        </mat-form-field>
                                        <mat-form-field appearance="outline">
                                          <mat-label>{{ 'MEETING_SETUP.TEMPLATE_REVIEW.ROLE' | translate }}</mat-label>
                                          <input matInput [(ngModel)]="editedAttendeeFormData.role" />
                                        </mat-form-field>
                                        @if (isGoverningBodyBoard) {
                                          <mat-form-field appearance="outline">
                                            <mat-label>{{ 'MEETING_SETUP.TEMPLATE_REVIEW.PLACEHOLDER_NAME' | translate }}</mat-label>
                                            <input matInput [(ngModel)]="editedAttendeeFormData.placeholderName" />
                                          </mat-form-field>
                                        }
                                      </div>
                                      <div class="form-actions">
                                        <button mat-button (click)="cancelEditAttendee()">
                                          {{ 'COMMON.CANCEL' | translate }}
                                        </button>
                                        <button
                                          mat-flat-button
                                          color="primary"
                                          [disabled]="!editedAttendeeFormData.firstName || !editedAttendeeFormData.lastName"
                                          (click)="saveAttendee()"
                                        >
                                          {{ 'COMMON.SAVE' | translate }}
                                        </button>
                                      </div>
                                    </div>
                                  } @else {
                                    <!-- Display mode -->
                                    <div class="attendee-info">
                                      <span class="attendee-name">
                                        @if (attendee.honorifics) {
                                          {{ formatHonorific(attendee.honorifics) }}
                                        }
                                        {{ attendee.firstName }} {{ attendee.lastName }}</span
                                      >
                                      <div class="attendee-meta">
                                        <span class="attendee-type-badge" [attr.data-type]="attendee.type">
                                          {{ 'MEETING_SETUP.TEMPLATE_REVIEW.ATTENDEE_TYPES.' + attendee.type | translate }}
                                        </span>
                                        @if (attendee.role) {
                                          <span class="attendee-role">{{ attendee.role }}</span>
                                        }
                                        @if (isGoverningBodyBoard && attendee.placeholderName) {
                                          <span class="attendee-placeholder">{{ attendee.placeholderName }}</span>
                                        }
                                      </div>
                                    </div>
                                    @if (canEdit && !isParticipantExtractionInProgress) {
                                      <div class="attendee-actions">
                                        <button
                                          mat-icon-button
                                          mat-small-icon-button
                                          [matTooltip]="'COMMON.EDIT' | translate"
                                          (click)="startEditAttendee(attendee)"
                                        >
                                          <mat-icon>edit</mat-icon>
                                        </button>
                                        <button
                                          mat-icon-button
                                          mat-small-icon-button
                                          color="warn"
                                          [matTooltip]="'COMMON.DELETE' | translate"
                                          (click)="deleteAttendee(attendee)"
                                        >
                                          <mat-icon>delete</mat-icon>
                                        </button>
                                      </div>
                                    }
                                  }
                                </div>
                              }
                            </div>
                          </div>
                        }
                      </div>
                    }
                  </div>
                </mat-tab>

                <!-- Minuting Styles Tab -->
                <mat-tab>
                  <ng-template mat-tab-label>
                    <mat-icon>tune</mat-icon>
                    <span>{{ 'MEETING_SETUP.TEMPLATE_REVIEW.TABS.STYLES' | translate }}</span>
                  </ng-template>
                  <div class="tab-content styles-tab">
                    <app-minutes-styling
                      [namingDisabled]="attendeeNames.length === 0"
                      [honorificDisabled]="anyAttendeeMissingHonorific"
                      [disabled]="!canEdit"
                      [data]="call"
                      [governingBodyType]="call.agendaMetadata.governingBodyType"
                      [attendeeListHasMembersAndNonMembers]="call.attendeeListHasMembersAndNonMembers"
                      [enabledMinutesLanguages]="organization.enabledMinutesLanguages"
                      (update)="updateMinutesStyling($event)"
                    ></app-minutes-styling>
                  </div>
                </mat-tab>
              </mat-tab-group>
            </div>
          }
        </div>
      }

      <!-- Step 5: Upload Recording (only for uploaded calls, after template is confirmed or recording already uploaded) -->
      @if (isUploadedCall && call !== null && (hasRecordingUploaded || (currentStep >= 4 && call.templateApprovedByUser))) {
        <div class="step-card" id="step-4">
          <div class="step-header">
            <div class="step-header-content">
              <h3>
                {{ 'MEETING_SETUP.STEP_LABEL' | translate: { number: uploadRecordingStepNumber } }}
                {{ 'MEETING_SETUP.UPLOAD_RECORDING.STEP_TITLE' | translate }}
                <mat-icon class="info-icon" [matTooltip]="'MEETING_SETUP.UPLOAD_RECORDING.TOOLTIP' | translate">info_outline</mat-icon>
              </h3>
              <p>{{ 'MEETING_SETUP.UPLOAD_RECORDING.DESCRIPTION' | translate }}</p>
            </div>
          </div>

          <!-- Hidden file upload component for recording -->
          <app-file-upload
            #recordingFileUpload
            class="hidden-file-upload"
            [acceptedFileTypes]="['mp3', 'mp4', 'wav']"
            [maxSize]="10_000_000_000"
            type="recording"
            (fileChange)="onRecordingFileChange($event)"
            (progressChange)="onRecordingProgressChange($event)"
          ></app-file-upload>

          <div class="recording-upload-container">
            <!-- Upload Zone (visible unless actively uploading, importing, or recording already uploaded) -->
            @if (!recordingFileUploadComponent?.isUploading && !isImportingRecording && !hasRecordingUploaded) {
              <div
                class="recording-upload-zone"
                [class.dragging]="isDraggingRecording"
                [class.disabled]="!canEdit"
                (click)="canEdit && onRecordingUploadCardClick()"
                (dragover)="canEdit && onRecordingDragOver($event)"
                (dragleave)="canEdit && onRecordingDragLeave()"
                (drop)="canEdit && onRecordingDrop($event)"
              >
                <div class="upload-zone-icon">
                  <mat-icon>cloud_upload</mat-icon>
                </div>
                <span class="upload-zone-title">
                  {{
                    isDraggingRecording ? ('MEETING_SETUP.UPLOAD_RECORDING.DROP_HERE' | translate) : ('MEETING_SETUP.UPLOAD_RECORDING.UPLOAD_TITLE' | translate)
                  }}
                </span>
                <span class="upload-zone-description">{{ 'MEETING_SETUP.UPLOAD_RECORDING.UPLOAD_DESCRIPTION' | translate }}</span>
                <div class="upload-zone-formats">
                  <span class="format-group">
                    <mat-icon>videocam</mat-icon>
                    MP4
                  </span>
                  <span class="format-group">
                    <mat-icon>mic</mat-icon>
                    MP3, WAV
                  </span>
                </div>
              </div>
            }

            <!-- Uploading/Processing State -->
            @if ((recordingFileUploadComponent?.isUploading || isImportingRecording) && uploadingFile !== null) {
              <div class="recording-file-card uploading">
                <div class="file-icon">
                  <mat-icon>{{ uploadingFile.isVideo ? 'videocam' : 'mic' }}</mat-icon>
                </div>
                <div class="file-info">
                  <div class="file-name-row">
                    <span class="file-name">{{ uploadingFile.name }}</span>
                  </div>
                  <div class="file-meta">
                    <span class="file-size">{{ uploadingFile.size | filesize }}</span>
                    @if (recordingUploadProgress < 100 && recordingRemainingTimeSeconds !== null) {
                      <span class="time-remaining">{{ 'COMMON.TIME_REMAINING' | translate: { time: (recordingRemainingTimeSeconds | duration: true) } }}</span>
                    }
                  </div>
                  <div class="file-progress">
                    @if (recordingUploadProgress >= 100 || isImportingRecording) {
                      <mat-progress-bar mode="indeterminate"></mat-progress-bar>
                      <div class="progress-details">
                        <span>{{ 'MEETING_SETUP.UPLOAD_RECORDING.PROCESSING' | translate }}</span>
                      </div>
                    } @else {
                      <mat-progress-bar mode="determinate" [value]="recordingUploadProgress"></mat-progress-bar>
                      <div class="progress-details">
                        <span>{{ 'MEETING_SETUP.UPLOAD_RECORDING.UPLOADING_PROGRESS' | translate: { progress: recordingUploadProgress } }}</span>
                      </div>
                    }
                  </div>
                </div>
                @if (!isImportingRecording) {
                  <button mat-icon-button class="remove-button" (click)="cancelRecordingUpload()">
                    <mat-icon>close</mat-icon>
                  </button>
                }
              </div>
            }

            <!-- Uploaded State -->
            @if (hasRecordingUploaded) {
              <div class="recording-file-card uploaded">
                <div class="file-icon uploaded">
                  <mat-icon>{{ uploadedRecordingIsVideo ? 'videocam' : 'mic' }}</mat-icon>
                </div>
                <div class="file-info">
                  <div class="file-name-row">
                    <span class="file-name">{{ uploadedRecordingFileName ?? ('MEETING_SETUP.UPLOAD_RECORDING.RECORDING_UPLOADED' | translate) }}</span>
                    <mat-icon class="check-icon">check_circle</mat-icon>
                  </div>
                  <div class="file-meta">
                    <span class="file-size">{{ (uploadedRecordingSize ? uploadedRecordingSize : 0) | filesize }}</span>
                    <span class="upload-complete-badge">{{ 'MEETING_SETUP.UPLOAD_RECORDING.UPLOAD_COMPLETE' | translate }}</span>
                  </div>
                </div>
                <button mat-icon-button class="remove-button" (click)="removeUploadedRecording()" [disabled]="!canEdit">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            }

            <!-- Helper Text -->
            @if (!hasRecordingUploaded && !recordingFileUploadComponent?.isUploading) {
              <p class="upload-helper-text">{{ 'MEETING_SETUP.UPLOAD_RECORDING.MAX_FILE_SIZE' | translate }}</p>
            }
          </div>
        </div>
      }
    </div>
  </div>
</div>
